import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../redux/store';
import { ToastContext } from '../utility/ToastContext';
import { Lesson } from '../models/Lesson';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { getSubjects } from '../redux/slices/subjectSlice';
import StaffDropDown from './StaffDropDown';
import ProgramDropDown from './ProgramDropDown';
import StageDropDown from './StageDropDown';
import ClassGroupDropDown from './ClassGroupDropDown';
import ProgramSubjectDropDown from './ProgramSubjectDropDown';
import DayOfWeekDropDown from './DayOfWeekDropDown';
import { getClassGroups } from '../redux/slices/classGroupSlice';
import { getStages } from '../redux/slices/stageSlice';
import TimePicker from 'react-time-picker';
import { ClassGroupParams } from '../models/classGroup';
import { showToastify } from '../utility/Toastify';
import { getLessons, updateLesson } from '../redux/slices/lessonSlice';
import { options } from '../models/utilities';
import '../css/ModernModal.css';

const TimeTableEditModal = (props: any) => {
    const { lesson, schoolId, branchId, isOpen, params, onRequestClose, setTimeTableEditModalOpen } = props;
    const { subjects } = useSelector((state: any) => state.subject)
    const dispatch = useDispatch<AppDispatch>()
    const { setShowToast } = useContext(ToastContext)

    // State initialization
    const [programId, setProgramId] = useState<number>(0);
    const [stageId, setStageId] = useState<number>(0);
    const [departmentId, setDepartmentId] = useState<number>(0);
    const [classGroupId, setClassGroupId] = useState<number>(0);
    const [staffId, setStaffId] = useState<number>(0);
    const [programSubjectId, setProgramSubjectId] = useState<number>(0);
    const [dayOfWeek, setDayOfWeek] = useState<string>('');
    
    // Start and end time state
    const [startTime, setStartTime] = useState<string>('07:45');
    const [endTime, setEndTime] = useState<string>('09:15');

    // Form data initialization
    const [formData, setFormData] = useState<Lesson>({
        id: lesson.id,
        class_group_id: lesson.class_group_id,
        staff_id: lesson.staff_id,
        program_subject_id: lesson.program_subject_id,
        day_of_week: lesson.day_of_week,
        start_time: lesson.start_time,
        end_time: lesson.end_time,
    });

    // Helper function to format time to "HH:mm"
    const formatTime = (time: string) => {
        const date = new Date(time);
        return date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
    };

    useEffect(() => {
        // Initialize start and end time with formatted values
        setFormData((prevData) => ({
            ...prevData,
            id: lesson.id,
            class_group_id: lesson.class_group_id,
            staff_id: lesson.staff_id,
            program_subject_id: lesson.program_subject_id,
            day_of_week: lesson.day_of_week,
            start_time: lesson.start_time,
            end_time: lesson.end_time,
        }));
        setStartTime(lesson.start_time ? formatTime(lesson.start_time) : formatTime(new Date().toISOString()));
        setEndTime(lesson.end_time ? formatTime(lesson.end_time) : formatTime(new Date().toISOString()));

    
        // Dispatch subjects fetching
        dispatch(getSubjects({
            ...params, school_id: schoolId, branch_id: branchId, pagination: {
                per_page: 100000,
                current_page: 1,
                total_items: 0,
                total_pages: 0
            }
        }));
    }, [branchId, dispatch, lesson, lesson.id, params, schoolId]);

    type AnyType = {
        [key: string]: string;
    };

    const handleInputChange = <T extends AnyType>(field: keyof T, value: string) => {
        // Update the formData state with the new value
        setFormData((prevData) => ({
            ...prevData,
            [field]: value,
        }));
        switch (field) {
            case 'stage_id':
                setStageId(parseInt(value));
                dispatch(getClassGroups({ school_id: schoolId, branch_id: branchId, program_id: programId, stage_id: parseInt(value), department_id: departmentId, pagination: { current_page: 1, per_page: 10000 }, paginate: false } as ClassGroupParams))
                break;
            case 'day_of_week':
                setDayOfWeek(value);
                break;
            case 'program_id':
                setProgramId(parseInt(value));
                if (branchId)
                    dispatch(getStages({ school_id: schoolId, branch_id: branchId, program_id: parseInt(value), pagination: { current_page: 1, per_page: 10000 }, paginate: false }))
                break;
            case 'staff_id':
                setStaffId(parseInt(value));
                break;
            case 'program_subject_id':
                setProgramSubjectId(parseInt(value));
                break;
            case 'class_group_id':
                setClassGroupId(parseInt(value));
                break;
            default:
                break;
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        dispatch(updateLesson({ ...formData, start_time: startTime, end_time: endTime }))
            .then((res) => {
                setShowToast(true);
                showToastify(res.payload.message, res.payload.status);
                dispatch(getLessons({
                    school_id: schoolId,
                    branch_id: branchId,
                    program_id: programId,
                    stage_id: stageId,
                    department_id: departmentId,
                    class_group_id: classGroupId,
                    staff_id: staffId,
                    program_subject_id: programSubjectId,
                    day_of_week: dayOfWeek,
                    pagination: params.pagination, paginate: true
                }))
                setTimeTableEditModalOpen(false)
            });
    };

    return (
        <Modal animation show={isOpen} centered onHide={onRequestClose} size='xl' className="modern-modal edit-modal">
            <Modal.Header closeButton>
                <Modal.Title><i className="fas fa-edit"></i> Edit: {lesson.class_group_name} - {lesson.program_subject_name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row className='d-flex flex-column flex-lg-row'>
                        <Col>
                            <StaffDropDown onChange={handleInputChange} branchId={0} value={lesson} schoolId={0} />
                        </Col>
                        <Col>
                            <ProgramDropDown admission={lesson} onChange={handleInputChange} departmentId={undefined} branchId={0} />
                        </Col>
                        <Col>
                            <StageDropDown lesson={lesson} onChange={handleInputChange} branchId={0} />
                        </Col>
                    </Row>
                    <Row className='d-flex flex-column flex-lg-row'>
                        <Col>
                            <ClassGroupDropDown lesson={lesson} onChange={handleInputChange} programId={programId} stageId={stageId} departmentId={departmentId} />
                        </Col>
                        <Col>
                            <ProgramSubjectDropDown onChange={handleInputChange} lesson={lesson} />
                        </Col>
                        <Col>
                            <DayOfWeekDropDown onChange={handleInputChange} lesson={lesson} />
                        </Col>
                    </Row>
                    <Row className='d-flex flex-column flex-lg-row justify-content-between mt-2'>
                        <Col md={4} className='d-flex flex-row gap-5'>
                            <span className='pt-2'>Start Time</span>
                            <span>
                                <TimePicker
                                    onChange={(e) => setStartTime(e as string)}
                                    value={startTime}
                                />
                            </span>
                        </Col>
                        <Col md={4} className='d-flex flex-row gap-5'>
                            &nbsp;
                        </Col>
                        <Col md={4} className='d-flex flex-row gap-5'>
                            <span className='pt-2'>End Time</span>
                            <span>
                                <TimePicker
                                    onChange={(e) => setEndTime(e as string)}
                                    value={endTime}
                                />
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <button type="submit" className="btn btn-primary mt-2"><i className="fas fa-save"></i> Save</button>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default TimeTableEditModal;
